import { KeyboardBackspace } from '@mui/icons-material';
import { Paper, Stack, Typography, Divider, Button, CircularProgress, TextField, Link } from '@mui/material';
import { Dialog, GA4CheckList, GA4CheckListItem, InfoMessage } from 'components';
import { useGa4, useNotifications, useUsers } from 'hooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { additionalErrorTexts, getAnalyticsAccountChecklist, inputAccordionData, numberInputStyling } from 'shared';
import { RouteList } from 'types';

export const EditUserGa4 = () => {
  const navigate = useNavigate();
  const { updateUserGa4PropertyIdMutation, checkUserGa4ConnectionMutation, isUserConnectionSuccess } = useGa4();
  const { userProfileQuery } = useUsers();
  const { setNotification } = useNotifications();

  const currentGa4PropertyId = userProfileQuery.data?.user.defaultGa4PropertyId;

  const [isInitialPropSettled, setIsInitialPropSettled] = useState(false);
  const [isPropertyIdInvalid, setIsPropertyIdInvalid] = useState(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [ga4PropertyId, setGa4PropertyId] = useState('');
  const [checkedGa4PropertyId, setCheckedGa4PropertyId] = useState('');

  const isPropertyIdChanged = currentGa4PropertyId !== ga4PropertyId;

  useEffect(() => {
    if (!ga4PropertyId && currentGa4PropertyId && !isInitialPropSettled) {
      setGa4PropertyId(currentGa4PropertyId);
      setIsInitialPropSettled(true);
    }
  }, [ga4PropertyId, isInitialPropSettled, setGa4PropertyId, currentGa4PropertyId]);

  useEffect(() => {
    if (currentGa4PropertyId) {
      setCheckedGa4PropertyId(currentGa4PropertyId);
    }
  }, [currentGa4PropertyId]);

  useEffect(() => {
    if (isUserConnectionSuccess) {
      setCheckedGa4PropertyId(ga4PropertyId);
    }

    // update the checked GA4 property ID only when user connection was successful
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserConnectionSuccess]);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const changeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.value.length) {
        setIsPropertyIdInvalid(true);
      } else {
        setIsPropertyIdInvalid(false);
      }
      setGa4PropertyId(e.target.value);
    },
    [setGa4PropertyId]
  );

  const setInputError = () => {
    setIsPropertyIdInvalid(true);
    inputRef.current && inputRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const onSubmit = async () => {
    if (!ga4PropertyId) {
      setInputError();
      return;
    } else {
      setIsPropertyIdInvalid(false);
    }
    await updateUserGa4PropertyIdMutation.mutateAsync(ga4PropertyId);
    setNotification('Deine GA4 Property ID wurde erfolgreich hinzugefügt!', 'success');
    navigate(RouteList.Profile);
  };

  const GA4Config = useMemo(() => {
    return getAnalyticsAccountChecklist({
      checkGa4ConnectionMutation: () => checkUserGa4ConnectionMutation.mutate(ga4PropertyId),
      PropertyIdInput: null,
      setInputError,
      shouldShowAdditionalError: isUserConnectionSuccess === false,
      ga4PropertyId,
    });
  }, [checkUserGa4ConnectionMutation, ga4PropertyId, isUserConnectionSuccess]);

  const onBackClick = () => {
    if (!isPropertyIdChanged) {
      navigate(RouteList.Profile);
    } else {
      setIsExitDialogOpen(true);
    }
  };

  const onPrimaryActionClick = () => {
    navigate(RouteList.Profile);
  };

  const onSecondaryActionClick = async () => {
    onSubmit();
  };

  const onTertiaryActionClick = () => {
    setIsExitDialogOpen(false);
  };

  return (
    <Paper sx={{ p: 5 }}>
      <Stack pb={5} alignItems="start">
        <Button
          variant="text"
          startIcon={<KeyboardBackspace color="primary" />}
          sx={(theme) => ({ textTransform: 'none', maxHeight: theme.spacing(8) })}
          onClick={onBackClick}
        >
          Zurück, ohne zu speichern
        </Button>
        <Typography variant="h2">Google Analytics 4 mit Alloy verbinden</Typography>
      </Stack>
      <Divider />
      <Stack gap={6} py={6} width={{ md: 2 / 3, sm: 1 }}>
        <InfoMessage
          fullWidth
          variant="info"
          title="Du benötigst GA4-Zugriff und Adminrechte, um die Verbindung herzustellen."
          message={
            <Typography>
              Brauchst du Hilfe?{' '}
              <Link href={RouteList.Contact} color="inherit" target="_blank">
                Unser Support-Team ist für dich da
              </Link>
              .
            </Typography>
          }
        />
        {GA4Config[0] && <GA4CheckList checkList={[GA4Config[0]]} />}
        <GA4CheckListItem
          checkListItem={{
            title: '2. Schritt - Gib hier die GA4 Property-ID ein',
            additionalError: isUserConnectionSuccess === false ? additionalErrorTexts : null,
            description: 'Gib die Property-ID ein zu der Ringier Advertising  Zugang erhalten soll.',
            Content: () => null,
            accordion: {
              Content: () => inputAccordionData,
            },
          }}
        >
          <TextField
            ref={inputRef}
            autoComplete="off"
            label="Property ID"
            value={ga4PropertyId}
            error={isPropertyIdInvalid}
            helperText={isPropertyIdInvalid ? 'Gib bitte die Property-ID ein' : ''}
            type="number"
            sx={{
              width: { xs: 1, md: 1 / 3 },
              mb: 2,
              ...numberInputStyling,
              '& .MuiInputBase-input.MuiInputBase-input': { background: '#ffffff' },
            }}
            onChange={changeHandler}
          />
        </GA4CheckListItem>
        {GA4Config[2] && GA4Config[3] && <GA4CheckList checkList={[GA4Config[2], GA4Config[3]]} />}
        {isUserConnectionSuccess && (
          <InfoMessage
            fullWidth
            variant="success"
            title="Super, die Verbindung hat geklappt!"
            message={<Typography>Klicke auf “Speichern” um den Vorgang abzuschliessen.</Typography>}
          />
        )}
        {isUserConnectionSuccess === false && (
          <InfoMessage
            fullWidth
            variant="error"
            title="Verbindung fehlgeschlagen"
            message={
              <Typography>
                Ooops, etwas ist schiefgelaufen. Überprüfe die Schritte oben und versuch es nochmals. <br />
                Benötigst du Unterstützung?{' '}
                <Link href={RouteList.Contact} color="inherit" target="_blank">
                  Hier findest du weitere Hilfe.
                </Link>
              </Typography>
            }
          />
        )}
      </Stack>

      <Divider />

      <Stack pt={10} direction="row" spacing={5} justifyContent="flex-end">
        <Button variant="outlined" onClick={onBackClick}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isPropertyIdChanged && checkedGa4PropertyId !== ga4PropertyId}
        >
          {updateUserGa4PropertyIdMutation.isPending ? <CircularProgress color="inherit" size={20} /> : 'Speichern'}
        </Button>
      </Stack>
      <Dialog
        open={isExitDialogOpen}
        title="Ungespeicherte Änderungen"
        content={
          <Typography>
            Du hast Änderungen vorgenommen, die noch nicht gespeichert sind. Wenn du jetzt gehst, gehen sie verloren.
          </Typography>
        }
        primaryActionTitle="ohne speichern verlassen"
        secondaryActionTitle="Speichern"
        onSecondaryActionClick={onSecondaryActionClick}
        onPrimaryActionClick={onPrimaryActionClick}
        tertiaryAction={{ tertiaryActionTitle: 'Abbrechen', onTertiaryActionClick: onTertiaryActionClick }}
        sx={{ '& .MuiPaper-root': { maxWidth: '540px' } }}
      />
    </Paper>
  );
};
